class BrowserDetectionBanner extends HTMLElement {
  constructor() {
    super();
    
    // Performance: Only initialize if we detect an in-app browser
    if (!this.isInAppBrowser()) {
      this.remove(); // Remove from DOM if not needed
      return;
    }
    
    this.dismissalKey = 'browser-banner-dismissed';
    this.dismissalDuration = 24 * 60 * 60 * 1000; // 24 hours
    
    // Check if banner was recently dismissed
    if (this.wasRecentlyDismissed()) {
      this.remove();
      return;
    }
    
    this.setupEventListeners();
    this.showBanner();
  }
  
  isInAppBrowser() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // First, exclude legitimate browsers
    // Safari on iOS
    if (ua.indexOf('Safari') > -1 && ua.indexOf('CriOS') === -1 && ua.indexOf('FxiOS') === -1 && ua.indexOf('FBAN') === -1 && ua.indexOf('Instagram') === -1) {
      return false;
    }
    
    // Chrome on any platform
    if (ua.indexOf('Chrome') > -1 && ua.indexOf('FBAN') === -1 && ua.indexOf('Instagram') === -1) {
      return false;
    }
    
    // Firefox
    if (ua.indexOf('Firefox') > -1 && ua.indexOf('FBAN') === -1) {
      return false;
    }
    
    // Edge
    if (ua.indexOf('Edg') > -1) {
      return false;
    }
    
    // Now check for in-app browsers
    // Facebook in-app browser
    if (ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1) {
      return true;
    }
    
    // Instagram in-app browser
    if (ua.indexOf('Instagram') > -1) {
      return true;
    }
    
    // Twitter/X in-app browser
    if (ua.indexOf('Twitter') > -1) {
      return true;
    }
    
    // LinkedIn in-app browser
    if (ua.indexOf('LinkedIn') > -1) {
      return true;
    }
    
    // TikTok in-app browser
    if (ua.indexOf('TikTok') > -1 || ua.indexOf('BytedanceWebview') > -1) {
      return true;
    }
    
    // Pinterest in-app browser
    if (ua.indexOf('Pinterest') > -1) {
      return true;
    }
    
    // Snapchat in-app browser  
    if (ua.indexOf('Snapchat') > -1) {
      return true;
    }
    
    // Only check for WebView if we haven't already identified a legitimate browser
    if (ua.indexOf('wv') > -1 && ua.indexOf('Android') > -1) {
      // Android WebView
      return true;
    }
    
    return false;
  }
  
  wasRecentlyDismissed() {
    try {
      const dismissedTime = sessionStorage.getItem(this.dismissalKey);
      if (!dismissedTime) return false;
      
      const timeSinceDismissal = Date.now() - parseInt(dismissedTime, 10);
      return timeSinceDismissal < this.dismissalDuration;
    } catch (e) {
      // If sessionStorage is not available, don't show banner
      return true;
    }
  }
  
  setupEventListeners() {
    this.copyButton = this.querySelector('.browser-banner__copy');
    this.closeButton = this.querySelector('.browser-banner__close');
    this.instructionsEl = this.querySelector('.browser-banner__instructions');
    
    if (this.copyButton) {
      this.copyButton.addEventListener('click', this.handleCopyLink.bind(this));
    }
    
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleDismiss.bind(this));
    }
    
    // Keyboard accessibility
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.handleDismiss();
      }
    });
    
    // Hide banner after sustained scrolling
    let scrollStartTime = null;
    let totalScrollDistance = 0;
    let lastScrollTop = 0;
    let scrollCheckInterval = null;
    
    const checkScrollBehavior = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);
      
      if (scrollDelta > 5) { // User is actively scrolling
        if (!scrollStartTime) {
          scrollStartTime = Date.now();
          totalScrollDistance = 0;
        }
        
        totalScrollDistance += scrollDelta;
        lastScrollTop = currentScrollTop;
        
        // Check if user has scrolled significantly for a sustained period
        const scrollDuration = Date.now() - scrollStartTime;
        const significantScroll = totalScrollDistance > 300; // pixels
        const sustainedTime = scrollDuration > 1500; // 1.5 seconds
        
        if (significantScroll && sustainedTime && this.classList.contains('animate-in')) {
          // User is reading content, not interested in banner
          this.classList.remove('animate-in');
          this.classList.add('hidden-by-scroll');
          
          // Clean up interval
          if (scrollCheckInterval) {
            clearInterval(scrollCheckInterval);
            scrollCheckInterval = null;
          }
        }
      } else {
        // User stopped scrolling, reset tracking
        if (Date.now() - scrollStartTime > 500) { // 500ms of no scrolling
          scrollStartTime = null;
          totalScrollDistance = 0;
        }
      }
    };
    
    // Start monitoring when user begins scrolling
    window.addEventListener('scroll', () => {
      if (!scrollCheckInterval && this.classList.contains('animate-in')) {
        lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        scrollCheckInterval = setInterval(checkScrollBehavior, 100);
      }
    }, { passive: true });
  }
  
  showBanner() {
    const delay = parseInt(this.dataset.delay || '0', 10);
    
    // Use requestAnimationFrame for smooth animation
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.classList.add('animate-in');
        
        // Announce to screen readers
        this.setAttribute('aria-live', 'polite');
        this.setAttribute('aria-atomic', 'true');
        
        // Focus management for accessibility
        const firstButton = this.querySelector('button');
        if (firstButton) {
          firstButton.focus();
        }
      }, delay);
    });
  }
  
  async handleCopyLink() {
    const url = window.location.href;
    
    try {
      // Use modern clipboard API with fallback
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      // Show success state
      this.copyButton.classList.add('copied');
      
      // Show instructions
      if (this.instructionsEl) {
        this.instructionsEl.hidden = false;
      }
      
      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = 'Link copied to clipboard';
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        announcement.remove();
      }, 1000);
      
      // Reset button state after delay
      setTimeout(() => {
        this.copyButton.classList.remove('copied');
      }, 3000);
      
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback: show the URL for manual copying
      if (this.instructionsEl) {
        this.instructionsEl.innerHTML = `<p>Copy this link: <code>${url}</code></p>`;
        this.instructionsEl.hidden = false;
      }
    }
  }
  
  handleDismiss() {
    // Store dismissal time
    try {
      sessionStorage.setItem(this.dismissalKey, Date.now().toString());
    } catch (e) {
      // SessionStorage not available, continue anyway
    }
    
    // Animate out
    this.classList.remove('animate-in');
    
    // Remove from DOM after animation
    this.addEventListener('transitionend', () => {
      this.remove();
    }, { once: true });
    
    // Announce dismissal to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = 'Browser recommendation dismissed';
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }
}

// Only define if not already defined
if (!customElements.get('browser-detection-banner')) {
  customElements.define('browser-detection-banner', BrowserDetectionBanner);
}

// Initialize immediately if DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Force upgrade of any existing elements
    const banners = document.querySelectorAll('browser-detection-banner');
    banners.forEach(banner => {
      if (banner && !banner.initialized) {
        banner.initialized = true;
      }
    });
  });
} else {
  // DOM already loaded, upgrade immediately
  const banners = document.querySelectorAll('browser-detection-banner');
  banners.forEach(banner => {
    if (banner && !banner.initialized) {
      banner.initialized = true;
    }
  });
}